import { useState, useEffect } from 'react';
import { 
  isPushSupported, 
  areNotificationsEnabled, 
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  getPushSubscription,
  showLocalNotification,
  updateUserTimezone
} from '@/utils/notifications';
import { getToken } from '@/utils/api';
import { getDailyQuote } from '@/utils/bossQuotes';

/**
 * NotificationPrompt - Shows on PWA install only (standalone mode)
 * For the original PWA-only notification prompt
 */
export function NotificationPrompt({ onClose }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  
  useEffect(() => {
    // Check if app is installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsInstalled(isStandalone);
    setIsSupported(isPushSupported());
    setIsEnabled(areNotificationsEnabled());
    
    // Register service worker on mount
    registerServiceWorker();
    
    // Update timezone on mount
    const token = getToken();
    updateUserTimezone(token);
  }, []);
  
  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      // Request permission
      const granted = await requestNotificationPermission();
      
      if (granted) {
        // Register service worker
        await registerServiceWorker();
        
        // Subscribe to push
        const token = getToken();
        await subscribeToPush(token);
        
        setIsEnabled(true);
        
        // Show test notification
        const quote = getDailyQuote();
        await showLocalNotification('Notifications Enabled! 🔥', {
          body: `Daily reminders active. "${quote.quote}" - ${quote.author}`,
          tag: 'welcome-notification'
        });
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Don't show if not installed as PWA
  if (!isInstalled) {
    return null;
  }
  
  // Don't show if not supported
  if (!isSupported) {
    return null;
  }
  
  // Don't show if already enabled
  if (isEnabled) {
    return null;
  }
  
  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl shadow-2xl p-5 z-50 animate-in slide-in-from-bottom duration-300">
      <button 
        onClick={onClose}
        className="absolute top-2 right-2 text-white/70 hover:text-white text-xl"
      >
        ✕
      </button>
      
      <div className="flex items-start gap-4">
        <div className="text-4xl">🔔</div>
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1">Stay on Track!</h3>
          <p className="text-sm text-white/90 mb-3">
            Get daily reminders to keep your streak going! We'll send you the quote of the day too. 💪
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={handleEnableNotifications}
              disabled={isLoading}
              className="flex-1 py-2 bg-white text-orange-600 font-bold rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Enabling...' : 'Enable Reminders'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/20 text-white font-medium rounded-lg hover:bg-white/30 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * LoginNotificationPrompt - Shows on EVERY login for ALL users
 * Recurring modal that encourages users to enable notifications
 * Benefits highlighted: motivational quotes, streak reminders
 */
export function LoginNotificationPrompt({ onClose, userId }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  
  useEffect(() => {
    const checkState = async () => {
      setIsSupported(isPushSupported());
      setIsEnabled(areNotificationsEnabled());
      
      // Check if already subscribed
      const subscription = await getPushSubscription();
      setAlreadySubscribed(!!subscription);
    };
    checkState();
  }, []);
  
  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      // Request permission
      const granted = await requestNotificationPermission();
      
      if (granted) {
        // Register service worker
        await registerServiceWorker();
        
        // Subscribe to push
        const token = getToken();
        await subscribeToPush(token);
        
        // Update timezone
        await updateUserTimezone(token);
        
        setIsEnabled(true);
        setAlreadySubscribed(true);
        
        // Show confirmation notification
        const quote = getDailyQuote();
        await showLocalNotification('Notifications Enabled! 🔔', {
          body: `"${quote.quote}" - ${quote.author}`,
          tag: 'welcome-notification'
        });
        
        // Save preference to not show again for 7 days
        if (userId) {
          localStorage.setItem(`loginNotificationPrompt_${userId}`, Date.now().toString());
        }
        
        onClose();
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDismiss = () => {
    // Save dismissal - will show again next login
    if (userId) {
      localStorage.setItem(`loginNotificationPrompt_${userId}`, Date.now().toString());
    }
    onClose();
  };
  
  const handleNeverAsk = () => {
    // Save permanent dismissal
    if (userId) {
      localStorage.setItem(`loginNotificationPrompt_${userId}_never`, 'true');
    }
    onClose();
  };
  
  // Don't show if notifications not supported
  if (!isSupported) {
    return null;
  }
  
  // Don't show if already subscribed
  if (alreadySubscribed) {
    return null;
  }
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={handleDismiss}
      data-testid="login-notification-prompt"
    >
      <div 
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl relative overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Decorative gradient top */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 opacity-20" />
        
        <div className="relative">
          {/* Close button */}
          <button 
            onClick={handleDismiss}
            className="absolute -top-1 -right-1 text-gray-400 hover:text-gray-600 text-xl"
            aria-label="Close"
          >
            ✕
          </button>
          
          {/* Icon */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-orange-200">
              <span className="text-3xl">🔔</span>
            </div>
          </div>
          
          {/* Headline */}
          <h2 className="text-2xl font-black text-gray-900 text-center mb-2">
            Turn On Notifications
          </h2>
          
          {/* Subtitle */}
          <p className="text-gray-500 text-center mb-6">
            Stay motivated and never miss a beat!
          </p>
          
          {/* Benefits list */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 mb-6 border border-amber-100">
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-lg">💪</span>
                <div>
                  <p className="font-semibold text-gray-800">Daily Motivational Quotes</p>
                  <p className="text-gray-500">Start each day with wisdom from Jay-Z, Nipsey, and more</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-lg">🔥</span>
                <div>
                  <p className="font-semibold text-gray-800">Streak Reminders</p>
                  <p className="text-gray-500">Never break your streak - we'll remind you before midnight</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-lg">🏆</span>
                <div>
                  <p className="font-semibold text-gray-800">Achievement Alerts</p>
                  <p className="text-gray-500">Celebrate your wins with instant badge notifications</p>
                </div>
              </li>
            </ul>
          </div>
          
          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleEnableNotifications}
              disabled={isLoading}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-lg rounded-2xl shadow-lg shadow-orange-200 hover:shadow-orange-300 transition-all disabled:opacity-50"
              data-testid="enable-notifications-btn"
            >
              {isLoading ? 'Enabling...' : 'Yes, Keep Me Motivated! 🚀'}
            </button>
            
            <button
              onClick={handleDismiss}
              className="w-full py-3 text-gray-500 font-medium hover:text-gray-700 transition-colors"
            >
              Maybe Later
            </button>
            
            <button
              onClick={handleNeverAsk}
              className="w-full py-2 text-gray-400 text-sm hover:text-gray-500 transition-colors"
            >
              Don't ask again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotificationSettings() {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reminderTime, setReminderTime] = useState('12:00');
  const [error, setError] = useState(null);
  const [debugLog, setDebugLog] = useState([]);
  const [preferences, setPreferences] = useState({
    bill_reminders: true,
    streak_reminders: true,
    envelope_prompts: true,
    budget_alerts: true,
    household_updates: true,
    weekly_summary: true,
    achievement_alerts: true,
    daily_quotes: true,
  });
  const [prefLoading, setPrefLoading] = useState(true);
  
  const addLog = (msg) => {
    setDebugLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`]);
  };
  
  useEffect(() => {
    setIsSupported(isPushSupported());
    setIsEnabled(areNotificationsEnabled());
    
    const checkSubscription = async () => {
      const subscription = await getPushSubscription();
      setIsSubscribed(!!subscription);
    };
    
    checkSubscription();
    
    const savedTime = localStorage.getItem('streakReminderTime');
    if (savedTime) {
      setReminderTime(savedTime);
    }

    // Fetch notification preferences
    const fetchPrefs = async () => {
      try {
        const token = getToken();
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/settings`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.preferences && Object.keys(data.preferences).length > 0) {
            setPreferences(prev => ({ ...prev, ...data.preferences }));
          }
        }
      } catch (e) {
        console.error('Error fetching notification preferences:', e);
      } finally {
        setPrefLoading(false);
      }
    };
    fetchPrefs();
  }, []);
  
  const handleToggleNotifications = async () => {
    setIsLoading(true);
    setError(null);
    setDebugLog([]);
    
    try {
      if (isSubscribed) {
        addLog('Unsubscribing...');
        const token = getToken();
        await unsubscribeFromPush(token);
        setIsSubscribed(false);
        addLog('Unsubscribed successfully');
      } else {
        addLog('Starting notification enable flow...');
        
        addLog('Verifying session...');
        const token = getToken();
        if (!token) {
          setError('Not logged in. Please log in first.');
          setIsLoading(false);
          return;
        }
        
        try {
          const authCheck = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });
          
          if (!authCheck.ok) {
            addLog('Session expired');
            setError('Your session has expired. Please log out and log back in to enable notifications.');
            setIsLoading(false);
            return;
          }
          addLog('Session valid');
        } catch (e) {
          addLog(`Session check failed: ${e.message}`);
          setError('Could not verify session. Please try again.');
          setIsLoading(false);
          return;
        }
        
        if (!isEnabled) {
          addLog('Requesting notification permission...');
          const granted = await requestNotificationPermission();
          addLog(`Permission result: ${granted ? 'GRANTED' : 'DENIED'}`);
          if (!granted) {
            setError('Notification permission denied. Please allow notifications in your browser settings.');
            setIsLoading(false);
            return;
          }
          setIsEnabled(true);
        } else {
          addLog('Permission already granted');
        }
        
        addLog('Registering service worker...');
        const swReg = await registerServiceWorker();
        addLog(`Service worker: ${swReg ? 'OK' : 'FAILED'}`);
        
        addLog('Subscribing to push...');
        try {
          const subscription = await subscribeToPush(token);
          addLog(`Subscription: ${subscription ? 'SUCCESS' : 'FAILED'}`);
          setIsSubscribed(!!subscription);
          
          if (subscription) {
            addLog('Showing confirmation notification...');
            const quote = getDailyQuote();
            await showLocalNotification('Streak Reminders Active!', {
              body: `You'll get daily reminders with the quote of the day.`,
              tag: 'reminder-enabled'
            });
            addLog('Done!');
          } else {
            setError('Failed to subscribe. See debug log below.');
          }
        } catch (subError) {
          addLog(`Subscribe error: ${subError.message}`);
          if (subError.message.includes('401') || subError.message.includes('Unauthorized')) {
            setError('Session expired. Please log out and log back in.');
          } else {
            setError(`Subscription failed: ${subError.message}`);
          }
        }
      }
    } catch (error) {
      addLog(`Error: ${error.message}`);
      setError(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleReminderTimeChange = async (e) => {
    const newTime = e.target.value;
    setReminderTime(newTime);
    localStorage.setItem('streakReminderTime', newTime);
    
    const token = getToken();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/reminder-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ reminder_time: newTime })
      });
    } catch (error) {
      console.error('Error updating reminder time:', error);
    }
  };
  
  const handleTestNotification = async () => {
    const token = getToken();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const quote = getDailyQuote();
        await showLocalNotification('Daily Streak Reminder', {
          body: `Don't forget to log your activity today! "${quote.quote}" - ${quote.author}`,
          tag: 'test-notification'
        });
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      const quote = getDailyQuote();
      await showLocalNotification('Daily Streak Reminder', {
        body: `Don't forget to log your activity today! "${quote.quote}" - ${quote.author}`,
        tag: 'test-notification'
      });
    }
  };

  const handlePrefToggle = async (key) => {
    const newVal = !preferences[key];
    const updated = { ...preferences, [key]: newVal };
    setPreferences(updated);

    try {
      const token = getToken();
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ preferences: { [key]: newVal } })
      });
    } catch (err) {
      console.error('Error updating preference:', err);
      setPreferences(prev => ({ ...prev, [key]: !newVal }));
    }
  };

  const PREF_ITEMS = [
    { key: 'bill_reminders', label: 'Bill Reminders', desc: 'Get notified when bills are due soon', icon: '💳' },
    { key: 'streak_reminders', label: 'Streak Reminders', desc: 'Daily reminders to keep your streak alive', icon: '🔥' },
    { key: 'envelope_prompts', label: 'Envelope Challenge Prompts', desc: 'Reminders for active savings challenges', icon: '✉️' },
    { key: 'budget_alerts', label: 'Budget Alerts', desc: 'Alerts for tight margins or overspending', icon: '📊' },
    { key: 'household_updates', label: 'Household Updates', desc: 'Activity from your shared budgets', icon: '🏠' },
    { key: 'weekly_summary', label: 'Weekly Summary', desc: 'A weekly recap of your financial progress', icon: '📋' },
    { key: 'achievement_alerts', label: 'Achievement Alerts', desc: 'Celebrate badges and milestones', icon: '🏆' },
    { key: 'daily_quotes', label: 'Daily Motivational Quotes', desc: 'Start your day with wisdom', icon: '💪' },
  ];

  if (!isSupported) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">🔔</span>
          <div>
            <h3 className="font-bold text-gray-900">Notification Preferences</h3>
            <p className="text-sm text-gray-500">Control which notifications you receive</p>
          </div>
        </div>
        <p className="text-gray-500 text-sm">
          Push notifications are not supported on this browser/device. Email notifications are still active based on your preferences below.
        </p>
        {/* Still show email preference toggles */}
        <div className="mt-4 space-y-1" data-testid="notification-preferences">
          {PREF_ITEMS.map(item => (
            <div key={item.key} className="flex items-center justify-between py-3 border-t border-gray-100 first:border-t-0">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <span className="text-lg mt-0.5">{item.icon}</span>
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500 truncate">{item.desc}</p>
                </div>
              </div>
              <button
                onClick={() => handlePrefToggle(item.key)}
                data-testid={`pref-toggle-${item.key}`}
                className={`relative flex-shrink-0 w-12 h-7 rounded-full transition-colors ${
                  preferences[item.key] ? 'bg-green-500' : 'bg-gray-300'
                }`}
                aria-label={`Toggle ${item.label}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                  preferences[item.key] ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">🔔</span>
        <div>
          <h3 className="font-bold text-gray-900">Notification Preferences</h3>
          <p className="text-sm text-gray-500">Control which notifications you receive</p>
        </div>
      </div>
      
      {/* Push Notification Toggle */}
      <div className="flex items-center justify-between py-3 border-t border-gray-100">
        <div>
          <p className="font-medium text-gray-800">Push Notifications</p>
          <p className="text-sm text-gray-500">
            {isSubscribed ? 'Receiving push notifications' : 'Enable push notifications'}
          </p>
        </div>
        <button
          onClick={handleToggleNotifications}
          disabled={isLoading}
          data-testid="push-notification-toggle"
          className={`relative w-14 h-8 rounded-full transition-colors ${
            isSubscribed ? 'bg-green-500' : 'bg-gray-300'
          } ${isLoading ? 'opacity-50' : ''}`}
        >
          <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
            isSubscribed ? 'translate-x-7' : 'translate-x-1'
          }`} />
        </button>
      </div>
      
      {/* Reminder Time */}
      {isSubscribed && (
        <div className="py-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800">Reminder Time</p>
              <p className="text-sm text-gray-500">When should we remind you?</p>
            </div>
            <input
              type="time"
              value={reminderTime}
              onChange={handleReminderTimeChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="reminder-time-input"
            />
          </div>
        </div>
      )}
      
      {/* Granular Notification Preferences */}
      <div className="py-3 border-t border-gray-100">
        <p className="font-semibold text-gray-800 mb-2 text-sm">Notification Types</p>
        <div className="space-y-1" data-testid="notification-preferences">
          {PREF_ITEMS.map(item => (
            <div key={item.key} className="flex items-center justify-between py-2.5">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <span className="text-lg mt-0.5">{item.icon}</span>
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500 truncate">{item.desc}</p>
                </div>
              </div>
              <button
                onClick={() => handlePrefToggle(item.key)}
                disabled={prefLoading}
                data-testid={`pref-toggle-${item.key}`}
                className={`relative flex-shrink-0 w-12 h-7 rounded-full transition-colors ${
                  preferences[item.key] ? 'bg-green-500' : 'bg-gray-300'
                } ${prefLoading ? 'opacity-50' : ''}`}
                aria-label={`Toggle ${item.label}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                  preferences[item.key] ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>
      
      {/* Test Notification */}
      {isSubscribed && (
        <div className="pt-3 border-t border-gray-100">
          <button
            onClick={handleTestNotification}
            data-testid="test-notification-btn"
            className="w-full py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100 transition-colors"
          >
            Send Test Notification
          </button>
        </div>
      )}
      
      {error && (
        <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      
      {debugLog.length > 0 && (
        <div className="mt-4 p-3 bg-gray-900 rounded-lg">
          <p className="text-xs text-gray-400 mb-2 font-bold">Debug Log:</p>
          {debugLog.map((log, i) => (
            <p key={i} className="text-xs text-green-400 font-mono">{log}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default NotificationSettings;
